/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';
import java from 'react-syntax-highlighter/dist/cjs/languages/prism/java';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
import json from 'react-syntax-highlighter/dist/cjs/languages/prism/json';
import python from 'react-syntax-highlighter/dist/cjs/languages/prism/python';
import r from 'react-syntax-highlighter/dist/cjs/languages/prism/r';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/cjs/prism-light';
import prism from 'react-syntax-highlighter/dist/cjs/styles/prism/prism';

import type { EmbeddedContentProps } from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedContent';
import EmbeddedLink from 'bundles/compound-assessments/components/shared/embeddedContent/EmbeddedLink';
import RenderPreviewShell from 'bundles/compound-assessments/components/shared/embeddedContent/RenderPreviewShell';
import RenderPreviewTitle from 'bundles/compound-assessments/components/shared/embeddedContent/RenderPreviewTitle';
import type { RenderPreviewAvailability } from 'bundles/compound-assessments/lib/utils/renderPreviewUtils';
import {
  getCodeBlockText,
  getLanguageTypeFromFileUrl,
  getRenderPreviewAvailability,
} from 'bundles/compound-assessments/lib/utils/renderPreviewUtils';

// register all supported language styling separately to ensure small bundle size
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('r', r);

const styles = {
  renderContainer: () => css`
    margin: var(--cds-spacing-200) 0;
  `,
  code: () => css`
    width: 100%;
    max-height: 52vh;
    overflow: auto;
  `,
};

export const EmbeddedCodeBlock: React.FC<EmbeddedContentProps> = ({ url, title }: EmbeddedContentProps) => {
  const [loading, setLoading] = useState(true);
  const [codeText, setCodeText] = useState<string | null>(null);
  const [languageType, setLanguageType] = useState<string | undefined>(undefined);
  const [previewAvailability, setPreviewAvailability] = useState<RenderPreviewAvailability | null>(null);

  useEffect(() => {
    const fetchFileForPreview = async () => {
      const previewAvailabilityResponse = await getRenderPreviewAvailability(url);
      setPreviewAvailability(previewAvailabilityResponse);
      if (previewAvailabilityResponse.fileAvailable && previewAvailabilityResponse.validFileSize) {
        const responseText = await getCodeBlockText(url);
        setLanguageType(getLanguageTypeFromFileUrl(url));
        setCodeText(responseText);
      }
      setLoading(false);
    };

    if (loading && !codeText) {
      fetchFileForPreview();
    }
  }, [codeText, loading, previewAvailability, url]);

  return (
    <div>
      <EmbeddedLink url={url} title={title} />
      <RenderPreviewShell
        loading={loading}
        fileAvailable={!!previewAvailability?.fileAvailable}
        validFileSize={!!previewAvailability?.validFileSize}
      >
        <div css={styles.renderContainer}>
          <RenderPreviewTitle />
          <SyntaxHighlighter
            language={languageType}
            style={prism}
            showLineNumbers={true}
            css={styles.code}
            title={title}
          >
            {codeText}
          </SyntaxHighlighter>
        </div>
      </RenderPreviewShell>
    </div>
  );
};

export const isEmbeddedCodeBlock = (url: string) => {
  const regex = /\.(txt|py|r|js|json|java|log)$/i;
  return url.match(regex);
};

export default EmbeddedCodeBlock;
